import { clientEnvModule } from "@/modules/client-env";
import { logger } from "@/modules/logger";
import posthog from "posthog-js";
import type { PostHog } from "posthog-js";

export class PosthogClientManager {
  private static _instance: PosthogClientManager | null = null;
  private _client: PostHog | null = null;
  private _isInitialized = false;

  static instance(): PosthogClientManager {
    if (!PosthogClientManager._instance) {
      PosthogClientManager._instance = new PosthogClientManager();
    }
    return PosthogClientManager._instance;
  }

  initialize(options?: { disableSessionRecording?: boolean }): void {
    if (this._isInitialized) {
      return;
    }

    const projectApiKey = clientEnvModule.posthogProjectApiKey();
    if (!projectApiKey) {
      logger.warn({ message: "PostHog project API key not found in environment variables" });
      return;
    }

    posthog.init(projectApiKey, {
      api_host: "https://us.i.posthog.com",
      disable_session_recording: options?.disableSessionRecording ?? false,
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
        },
      },
    });

    this._client = posthog;
    this._isInitialized = true;
  }

  get client(): PostHog | null {
    return this._client;
  }

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  identify(spaceAccountId: string, properties?: Record<string, unknown>): void {
    if (!this._client || !this._isInitialized) {
      return;
    }

    this._client.identify(spaceAccountId, properties);
  }

  alias(spaceAccountId: string, aliasId: string): void {
    if (!this._client || !this._isInitialized) {
      return;
    }
    this._client.alias(spaceAccountId, aliasId);
  }

  setPersonProperties(
    properties: Record<string, unknown>,
    once_properties?: Record<string, unknown>
  ): void {
    if (!this._client || !this._isInitialized) {
      return;
    }
    this._client.setPersonProperties(properties, once_properties);
  }

  reset(): void {
    if (!this._client || !this._isInitialized) {
      return;
    }

    this._client.reset();
  }

  shutdown(): void {
    if (!this._client || !this._isInitialized) {
      return;
    }

    // PostHog doesn't have a shutdown method, we just reset and clean up
    this._client.reset();
    this._isInitialized = false;
    this._client = null;
  }
}
